import {
  Add as AddIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { CardContainer } from "@msys/ui";
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import React from "react";
import { TextField } from "../../../../commons/form-fields/TextField.js";
import { useUnsavedChangesGuard } from "../../../../commons/hooks/useUnsavedChangesGuard.js";
import type { IntegrationSettingsProps } from "../../integrationRegistry.js";
import { getIntegrationMeta } from "../../integrationRegistry.js";
import { SettingsPageLayout } from "./SettingsPageLayout.js";
import { useIntegrationSettingsBase } from "./useIntegrationSettingsBase.js";
import { integrationErrorMessage } from "../../integrationErrorMessage.js";
import {
  CardSaveRow,
  FormDirtyTracker,
  useCardDirtyRegistry,
} from "./integrationCardForm.js";
import { useIntegrationSettings_M1LeadResponseQuery } from "./IntegrationSettings.generated.js";

function KeyValueEditor({
  value,
  onChange,
  keyLabel,
  valueLabel,
}: {
  value: Record<string, string>;
  onChange: (updated: Record<string, string>) => void;
  keyLabel: string;
  valueLabel: string;
}) {
  const [newKey, setNewKey] = React.useState("");
  const [newValue, setNewValue] = React.useState("");

  const entries = Object.entries(value);

  return (
    <Stack spacing={1}>
      {entries.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "center", px: 0.5 }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {keyLabel}
          </Typography>
          <Box sx={{ width: 24 }} />
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {valueLabel}
          </Typography>
          <Box sx={{ width: 34 }} />
        </Stack>
      )}
      {entries.map(([k, v]) => (
        <Stack
          key={k}
          direction="row"
          spacing={1}
          sx={{ alignItems: "center" }}
        >
          <MuiTextField
            value={k}
            disabled
            size="small"
            sx={{ flex: 1 }}
            slotProps={{
              input: { sx: { fontFamily: "monospace", fontSize: 13 } },
            }}
          />
          <Typography variant="caption" color="text.secondary">
            →
          </Typography>
          <MuiTextField
            value={v}
            onChange={e => {
              const updated = { ...value };
              updated[k] = e.target.value;
              onChange(updated);
            }}
            size="small"
            sx={{ flex: 1 }}
            slotProps={{
              input: { sx: { fontFamily: "monospace", fontSize: 13 } },
            }}
          />
          <IconButton
            size="small"
            onClick={() => {
              const updated = { ...value };
              delete updated[k];
              onChange(updated);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <MuiTextField
          value={newKey}
          onChange={e => setNewKey(e.target.value)}
          placeholder={keyLabel}
          size="small"
          sx={{ flex: 1 }}
          slotProps={{
            input: { sx: { fontFamily: "monospace", fontSize: 13 } },
          }}
        />
        <Typography variant="caption" color="text.secondary">
          →
        </Typography>
        <MuiTextField
          value={newValue}
          onChange={e => setNewValue(e.target.value)}
          placeholder={valueLabel}
          size="small"
          sx={{ flex: 1 }}
          slotProps={{
            input: { sx: { fontFamily: "monospace", fontSize: 13 } },
          }}
        />
        <IconButton
          size="small"
          disabled={!newKey.trim()}
          onClick={() => {
            if (!newKey.trim()) return;
            const updated = { ...value };
            updated[newKey.trim()] = newValue;
            onChange(updated);
            setNewKey("");
            setNewValue("");
          }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Stack>
  );
}

// -- Per-card form slices (each section card is its own form with its own
// Save/Cancel; a card's save merges its slice over the last saved values,
// so other cards' unsaved edits are never swept along) --

interface Meister1FormValues {
  flowUrl: string;
  templateMapping: Record<string, string>;
  importMapping: Record<string, string>;
  categoryMapping: Record<string, string>;
}

type FlowValues = Pick<Meister1FormValues, "flowUrl">;
type TemplateValues = Pick<Meister1FormValues, "templateMapping">;
type ImportValues = Pick<Meister1FormValues, "importMapping">;
type CategoryValues = Pick<Meister1FormValues, "categoryMapping">;

function pickFlow(v: Meister1FormValues): FlowValues {
  return { flowUrl: v.flowUrl };
}

function pickTemplates(v: Meister1FormValues): TemplateValues {
  return { templateMapping: v.templateMapping };
}

function pickImport(v: Meister1FormValues): ImportValues {
  return { importMapping: v.importMapping };
}

function pickCategories(v: Meister1FormValues): CategoryValues {
  return { categoryMapping: v.categoryMapping };
}

export const Meister1Settings = ({
  integrationId,
  mode,
}: IntegrationSettingsProps) => {
  const { t } = useTranslate(["Integrations", "Global"]);
  const meta = getIntegrationMeta(integrationId);
  const {
    client,
    orgIntegration,
    refetchDashboard,
    loading,
    removeLoading,
    handleAdd,
    handleUpdate,
    handleDisconnect,
    enqueueSnackbar,
  } = useIntegrationSettingsBase(integrationId, mode);

  const { anyDirty, dirtyOf } = useCardDirtyRegistry();
  useUnsavedChangesGuard(
    anyDirty,
    t("You have unsaved changes. Leave the page anyway?")
  );

  const [leadId, setLeadId] = React.useState("");
  const {
    data: leadData,
    loading: leadLoading,
    refetch: refetchLead,
  } = useIntegrationSettings_M1LeadResponseQuery({
    client,
    variables: { leadId: Number(leadId) },
    skip: !leadId || isNaN(Number(leadId)),
    fetchPolicy: "network-only",
  });

  const integrationName = meta.sidebarLabel ?? "Meister1";
  const settings = (orgIntegration?.settings as any) ?? {};

  if (mode === "create") {
    return (
      <SettingsPageLayout
        integrationId={integrationId}
        integrationName="Meister1"
        title={`${t("Connect")} Meister1`}
      >
        <Typography variant="body2" color="text.secondary">
          {t(meta.description as any)}
        </Typography>
        <Formik
          initialValues={{ foreignAccountId: "" }}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              await handleAdd(undefined, values.foreignAccountId);
            } catch (e) {
              enqueueSnackbar(
                integrationErrorMessage(
                  e,
                  t("Something went wrong. Please try again.", { ns: "Global" })
                ),
                { variant: "error" }
              );
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting }) => (
            <Form>
              <Stack spacing={2}>
                <TextField
                  name="foreignAccountId"
                  label="Foreign Account ID"
                  required
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "..." : t("Connect")}
                </Button>
              </Stack>
            </Form>
          )}
        </Formik>
      </SettingsPageLayout>
    );
  }

  // Baseline form values derived from the currently stored settings — a
  // card's save merges its slice over these, so it never sweeps other
  // cards' unsaved edits, but also never drops their saved values
  const initialValues: Meister1FormValues = {
    flowUrl: settings.flowUrl ?? "",
    templateMapping:
      (settings.m1CalculatorId2MsysTemplateId as Record<string, string>) ?? {},
    importMapping:
      (settings.m1CalculatorIdCreateM1Import as Record<string, string>) ?? {},
    categoryMapping:
      (settings.m1CalculatorIdProjectCategories as Record<string, string>) ??
      {},
  };

  const handleSave = async (values: Meister1FormValues) => {
    try {
      await handleUpdate({
        flowUrl: values.flowUrl,
        m1CalculatorId2MsysTemplateId: values.templateMapping,
        m1CalculatorIdCreateM1Import: values.importMapping,
        m1CalculatorIdProjectCategories: values.categoryMapping,
      });
      // handleUpdate doesn't refetch the dashboard query — refetch so
      // enableReinitialize resets the cards from the new stored settings
      await refetchDashboard();
    } catch (e) {
      enqueueSnackbar(
        integrationErrorMessage(
          e,
          t("Settings could not be saved. Please check your input.")
        ),
        { variant: "error" }
      );
    }
  };

  return (
    <SettingsPageLayout
      integrationId={integrationId}
      integrationName={integrationName}
      title={`${integrationName} — ${t("Settings")}`}
      onDisconnect={handleDisconnect}
      removeLoading={removeLoading}
      loading={loading && !orgIntegration}
      events={orgIntegration?.events ?? []}
      onRefreshEvents={refetchDashboard}
      hasDashboard={!!orgIntegration?.health}
    >
      <Stack spacing={2}>
        {/* Flowing section cards instead of tabs (2026-07-14 review).
            Each card is an independent form with its own Save/Cancel:
            a card's save merges its slice over the last saved values,
            so other cards' unsaved edits stay untouched. */}
        <Formik<FlowValues>
          initialValues={pickFlow(initialValues)}
          onSubmit={values => handleSave({ ...initialValues, ...values })}
          enableReinitialize
        >
          {fp => (
            <Form>
              <FormDirtyTracker
                dirty={fp.dirty}
                onDirtyChange={dirtyOf("flow")}
              />
              <CardContainer title={t("Flow")}>
                <Stack spacing={2} sx={{ px: 1, pt: 0.5, pb: 1 }}>
                  <TextField
                    name="flowUrl"
                    label={t("Flow URL")}
                    slotProps={{
                      input: {
                        sx: { fontFamily: "monospace", fontSize: 13 },
                      },
                    }}
                  />

                  {settings.flowUrl && (
                    <Button
                      size="small"
                      variant="outlined"
                      endIcon={<OpenInNewIcon />}
                      component="a"
                      target="_blank"
                      rel="noopener noreferrer"
                      href={settings.flowUrl}
                      sx={{ alignSelf: "flex-start" }}
                    >
                      {t("Open Flow UI")}
                    </Button>
                  )}

                  {/* Lead Inspector */}
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    {t("Inspect Lead Response")}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "flex-end" }}
                  >
                    <MuiTextField
                      label={t("Lead ID")}
                      value={leadId}
                      onChange={e => setLeadId(e.target.value)}
                      size="small"
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!leadId || leadLoading}
                      onClick={() => refetchLead()}
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      {t("Inspect")}
                    </Button>
                  </Stack>
                  {leadData?.m1ApiLeadResponse && (
                    <pre
                      style={{
                        fontSize: 12,
                        overflow: "auto",
                        maxHeight: 300,
                        background: "#f5f5f5",
                        padding: 8,
                        borderRadius: 4,
                      }}
                    >
                      {JSON.stringify(leadData.m1ApiLeadResponse, null, 2)}
                    </pre>
                  )}

                  {/* External campaigns link */}
                  <Button
                    size="small"
                    variant="outlined"
                    endIcon={<OpenInNewIcon />}
                    component="a"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`${import.meta.env.VITE_M1_MY_URL}/campaigns`}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    {t("Go to campaigns settings")}
                  </Button>

                  <CardSaveRow fp={fp} />
                </Stack>
              </CardContainer>
            </Form>
          )}
        </Formik>

        <Formik<TemplateValues>
          initialValues={pickTemplates(initialValues)}
          onSubmit={values => handleSave({ ...initialValues, ...values })}
          enableReinitialize
        >
          {fp => (
            <Form>
              <FormDirtyTracker
                dirty={fp.dirty}
                onDirtyChange={dirtyOf("templates")}
              />
              <CardContainer title={t("Templates")}>
                <Stack spacing={2} sx={{ px: 1, pt: 0.5, pb: 1 }}>
                  <KeyValueEditor
                    value={fp.values.templateMapping}
                    onChange={updated =>
                      fp.setFieldValue("templateMapping", updated)
                    }
                    keyLabel={t("Calculator ID")}
                    valueLabel={t("Template ID")}
                  />
                  <CardSaveRow fp={fp} />
                </Stack>
              </CardContainer>
            </Form>
          )}
        </Formik>

        <Formik<ImportValues>
          initialValues={pickImport(initialValues)}
          onSubmit={values => handleSave({ ...initialValues, ...values })}
          enableReinitialize
        >
          {fp => (
            <Form>
              <FormDirtyTracker
                dirty={fp.dirty}
                onDirtyChange={dirtyOf("import")}
              />
              <CardContainer title={t("Import")}>
                <Stack spacing={2} sx={{ px: 1, pt: 0.5, pb: 1 }}>
                  <KeyValueEditor
                    value={fp.values.importMapping}
                    onChange={updated =>
                      fp.setFieldValue("importMapping", updated)
                    }
                    keyLabel={t("Calculator ID")}
                    valueLabel={t("Create Import")}
                  />
                  <CardSaveRow fp={fp} />
                </Stack>
              </CardContainer>
            </Form>
          )}
        </Formik>

        <Formik<CategoryValues>
          initialValues={pickCategories(initialValues)}
          onSubmit={values => handleSave({ ...initialValues, ...values })}
          enableReinitialize
        >
          {fp => (
            <Form>
              <FormDirtyTracker
                dirty={fp.dirty}
                onDirtyChange={dirtyOf("categories")}
              />
              <CardContainer title={t("Categories")}>
                <Stack spacing={2} sx={{ px: 1, pt: 0.5, pb: 1 }}>
                  <KeyValueEditor
                    value={fp.values.categoryMapping}
                    onChange={updated =>
                      fp.setFieldValue("categoryMapping", updated)
                    }
                    keyLabel={t("Calculator ID")}
                    valueLabel={t("Categories")}
                  />
                  <CardSaveRow fp={fp} />
                </Stack>
              </CardContainer>
            </Form>
          )}
        </Formik>
      </Stack>
    </SettingsPageLayout>
  );
};
